/**
 * Binary CDO Protocol Registry — shared config for protocols whose CDO is
 * sent as a FlatBuffer binary (`cdo_binary` + `cdo_schema_name`) rather than
 * JSON `cdo_input`. Currently OPCUA and LOBBYVISION2.
 *
 * Centralizes the per-protocol logic (schema name, validity predicate,
 * missing message, build/base64/decode, log strings) that was previously
 * duplicated across AdapterEditor.tsx, extractCdoState.ts, and
 * StepAdaptersCdoEditor.tsx. Consumers still select the correct
 * CdoStateBundle slot (cdoOpcUa vs cdoLobbyVision2) explicitly — the bundle
 * shape is intentionally out of scope for this registry.
 *
 * Do NOT add MODBUS/etc. here: MODBUS uses a different (template) binary
 * path and the others use JSON cdo_input. MCE has its own decode-only config,
 * outside BINARY_CDO_PROTOCOLS, because the frontend never packs a Mapping.
 */
import { AdapterCdoOpcUa } from "../../../interfaces/AdapterCdoOpcUa.interface";
import {
  AdapterCdoLobbyVision2,
  lobbyVision2RowCallTypeValid,
} from "../../../interfaces/AdapterCdoLobbyVision2.interface";
import {
  buildOpcUaMappingBytes,
  opcUaMappingBytesToBase64,
  decodeOpcUaMapping,
} from "../../../flatbuffers/builders/opcUaCdoBuilder";
import {
  buildLobbyVision2TemplateBytes,
  lobbyVision2TemplateBytesToBase64,
  decodeLobbyVision2Template,
} from "../../../flatbuffers/builders/lobbyVision2CdoBuilder";
import decodeStoredMceMapping from "../../../flatbuffers/decoders/mceMapping";
import {
  BLANK_ADAPTER_CDO_OPCUA,
  BLANK_ADAPTER_CDO_LOBBYVISION2,
} from "../../../constants/adapter";

/**
 * True when an OPC UA identity form is complete enough to build the
 * generated Identity union: Anonymous never needs fields; UserName and
 * X509 each need both of their fields non-blank.
 *
 * Relocated from StepAdaptersCdoEditor.tsx (its only former caller) so the
 * OPC UA validity predicate lives beside the rest of the OPC UA config.
 */
export function isOpcUaIdentityValid(identity: AdapterCdoOpcUa["identity"]): boolean {
  if (identity.kind === "anonymous") return true;
  if (identity.kind === "username") {
    return identity.username.trim() !== "" && identity.password.trim() !== "";
  }
  return identity.certificatePath.trim() !== "" && identity.privateKeyPath.trim() !== "";
}

/**
 * Per-protocol config for the binary-CDO pattern. `T` is the editor form
 * type for the protocol (AdapterCdoOpcUa | AdapterCdoLobbyVision2).
 */
export interface BinaryCdoProtocolConfig<T> {
  /** Uppercase protocol key matching protocolType.toUpperCase() (e.g. "OPCUA"). */
  protocolKey: string;
  /** cdo_schema_name sent in the create body (e.g. "opc_ua"). */
  schemaName: string;
  /** Blank form state for this protocol. */
  blank: T;
  /** Pack editor state to FlatBuffer bytes. */
  build: (editor: T) => Uint8Array;
  /** Base64-encode packed bytes for the cdo_binary field. */
  toBase64: (bytes: Uint8Array) => string;
  /** Decode FlatBuffer bytes back to editor state. */
  decode: (bytes: Uint8Array) => T;
  /** True when the editor state is complete enough to save. */
  isValid: (cdo: T) => boolean;
  /** Human-readable message describing what is missing. */
  missingMessage: string;
  /** Exact log prefix for a non-OK PUT response (byte-parity with prior code). */
  putFailedLog: string;
  /** Exact log prefix for the catch-block error (byte-parity with prior code). */
  errorLog: string;
}

export const OPC_UA_BINARY_CDO_CONFIG: BinaryCdoProtocolConfig<AdapterCdoOpcUa> = {
  protocolKey: "OPCUA",
  schemaName: "opc_ua",
  blank: BLANK_ADAPTER_CDO_OPCUA,
  build: buildOpcUaMappingBytes,
  toBase64: opcUaMappingBytesToBase64,
  decode: decodeOpcUaMapping,
  isValid: (cdo) =>
    isOpcUaIdentityValid(cdo.identity) && (cdo.nodeToMsg.length > 0 || cdo.msgToNode.length > 0),
  missingMessage: "OPC UA requires a complete identity and at least one node mapping",
  putFailedLog: "[OpcUaCDO] PUT failed:",
  errorLog: "[OpcUaCDO] sendOpcUaCdoBinary error:",
};

export const LOBBYVISION2_BINARY_CDO_CONFIG: BinaryCdoProtocolConfig<AdapterCdoLobbyVision2> = {
  protocolKey: "LOBBYVISION2",
  schemaName: "lobbyvision2",
  blank: BLANK_ADAPTER_CDO_LOBBYVISION2,
  build: buildLobbyVision2TemplateBytes,
  toBase64: lobbyVision2TemplateBytesToBase64,
  decode: decodeLobbyVision2Template,
  // Needs at least one mapping, and every call mapping — a call operationType
  // OR a Car Call / Hall Call / Secure Car Call LN2 message — must carry a
  // chosen SecureCarCalltype (in `ccType`). `data`/`floorsBased` are
  // independent of call type and do not affect this check.
  isValid: (cdo) =>
    (cdo.reads.length > 0 || cdo.writes.length > 0) &&
    cdo.reads.every(lobbyVision2RowCallTypeValid) &&
    cdo.writes.every(lobbyVision2RowCallTypeValid),
  missingMessage:
    "Add at least one LN2 → Telegraph or Telegraph → LN2 mapping entry, and choose a call type (front/rear car call or up/down hall call) for every Car Call, Hall Call, or Secure Car Call mapping",
  putFailedLog: "[LobbyVision2CDO] PUT failed:",
  errorLog: "[LobbyVision2CDO] sendLobbyVision2CdoBinary error:",
};

/**
 * MCE reads its binary and never writes it: new adapters come from an uploaded
 * IDL and the server builds the Mapping.
 */
export const MCE_BINARY_CDO_CONFIG = {
  protocolKey: "MCE",
  schemaName: "mce",
  decode: decodeStoredMceMapping,
} as const;

/**
 * Discriminated union across all binary-CDO protocol configs. A plain
 * BinaryCdoProtocolConfig<unknown>[] will not typecheck: T is invariant
 * (appears in both build's parameter and decode's return position).
 */
export type AnyBinaryCdoProtocolConfig =
  BinaryCdoProtocolConfig<AdapterCdoOpcUa> | BinaryCdoProtocolConfig<AdapterCdoLobbyVision2>;

export const BINARY_CDO_PROTOCOLS: AnyBinaryCdoProtocolConfig[] = [
  OPC_UA_BINARY_CDO_CONFIG,
  LOBBYVISION2_BINARY_CDO_CONFIG,
];

/** Lookup a binary-CDO config by uppercase protocol key (e.g. "OPCUA"). */
export const getBinaryCdoProtocolByKey = (
  protocolKey: string,
): AnyBinaryCdoProtocolConfig | undefined =>
  BINARY_CDO_PROTOCOLS.find((c) => c.protocolKey === protocolKey.toUpperCase());
